import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, X, Lock, Warning, Minus } from "@phosphor-icons/react";
import StockPartCombobox from "@/components/StockPartCombobox";

/**
 * Per-part quota editor for a single engineer/technician.
 * Managers can add/remove/edit rows; everyone else sees it read-only.
 */
export default function PartQuotasDialog({ open, engineer, isManager, onClose, onSaved }) {
  const [rows, setRows] = useState([]);   // {stock_code, name, group, uom, max_qty, held_qty}
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !engineer) return;
    setLoading(true);
    api.get(`/engineers/${engineer.id}/part-quotas`)
      .then((res) => setRows(res.data.quotas || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, engineer]);

  const addRow = () => setRows([...rows, {
    stock_code: null, name: "", group: null, uom: null, max_qty: 1, held_qty: 0,
  }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const setRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const pickPart = (i, picked) => setRows(rows.map((r, idx) => idx === i ? {
    ...r,
    name: picked.name,
    stock_code: picked.stock_code || null,
    group: picked.group || null,
    uom: picked.uom || null,
  } : r));

  const save = async () => {
    setBusy(true);
    try {
      const cleaned = rows
        .filter((r) => (r.name || "").trim() && Number(r.max_qty) > 0)
        .map((r) => ({
          stock_code: r.stock_code || null,
          name: r.name.trim(),
          group: r.group || null,
          uom: r.uom || null,
          max_qty: Math.max(1, parseInt(r.max_qty, 10) || 0),
        }));
      await api.put(`/engineers/${engineer.id}/part-quotas`, { quotas: cleaned });
      toast.success(`Saved ${cleaned.length} quota line${cleaned.length === 1 ? "" : "s"}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!engineer) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="part-quotas-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Lock size={20} weight="duotone" />
            Part Quotas — {engineer.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600 flex items-start gap-2">
            <Warning size={12} weight="bold" className="mt-0.5 shrink-0" />
            <div>
              Set the maximum number of units this person may carry <b>per part</b>.
              Storekeepers can't issue more than the cap for any part listed here.
              Parts not listed are unrestricted. Only a <b>Manager</b> can edit these
              caps.
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-neutral-400 font-mono text-xs">Loading quotas…</div>
          ) : (
            <div className="border border-neutral-200 bg-white">
              <table className="w-full text-sm" data-testid="quotas-table">
                <thead className="border-b border-neutral-200 bg-neutral-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 label-eyebrow">Part</th>
                    <th className="px-3 py-2 label-eyebrow text-right">Holding now</th>
                    <th className="px-3 py-2 label-eyebrow text-right">Max qty</th>
                    <th className="px-3 py-2 label-eyebrow w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-8 text-center text-neutral-400 text-xs">
                        No per-part quotas set. {isManager ? "Click Add row to start." : "Ask a Manager to configure."}
                      </td>
                    </tr>
                  )}
                  {rows.map((r, i) => {
                    const held = Number(r.held_qty || 0);
                    const cap = Number(r.max_qty || 0);
                    const over = cap > 0 && held > cap;
                    return (
                      <tr key={i} className="border-b border-neutral-100 last:border-0" data-testid={`quota-row-${i}`}>
                        <td className="px-3 py-2">
                          {isManager ? (
                            <StockPartCombobox
                              value={r}
                              onChange={(picked) => pickPart(i, picked)}
                              testid={`quota-picker-${i}`}
                              placeholder="Search master list…"
                            />
                          ) : (
                            <div>
                              <div className="text-xs font-medium">{r.name}</div>
                              {r.stock_code && <div className="text-[10px] font-mono text-neutral-500">{r.stock_code}</div>}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className={`font-mono text-xs ${over ? "text-red-700 font-semibold" : "text-neutral-700"}`}>
                            {held}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {isManager ? (
                            <div className="inline-flex items-center border border-neutral-400 h-8" data-testid={`quota-stepper-${i}`}>
                              <button
                                type="button"
                                onClick={() => setRow(i, "max_qty", Math.max(1, (parseInt(r.max_qty, 10) || 0) - 1))}
                                className="w-7 h-full flex items-center justify-center border-r border-neutral-400 hover:bg-neutral-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Decrease"
                                disabled={(parseInt(r.max_qty, 10) || 0) <= 1}
                                data-testid={`quota-dec-${i}`}
                              >
                                <Minus size={12} weight="bold" />
                              </button>
                              <Input
                                type="number"
                                min={1}
                                value={r.max_qty}
                                onChange={(e) => setRow(i, "max_qty", e.target.value)}
                                className={`w-14 h-full border-0 rounded-none font-mono text-right focus-visible:ring-0 ${over ? "text-red-700" : ""}`}
                                data-testid={`quota-max-${i}`}
                              />
                              <button
                                type="button"
                                onClick={() => setRow(i, "max_qty", (parseInt(r.max_qty, 10) || 0) + 1)}
                                className="w-7 h-full flex items-center justify-center border-l border-neutral-400 hover:bg-neutral-900 hover:text-white"
                                aria-label="Increase"
                                data-testid={`quota-inc-${i}`}
                              >
                                <Plus size={12} weight="bold" />
                              </button>
                            </div>
                          ) : (
                            <span className="font-mono text-xs">{r.max_qty}</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {isManager && (
                            <button
                              type="button"
                              onClick={() => removeRow(i)}
                              className="h-8 w-8 flex items-center justify-center border border-neutral-300 hover:border-red-600 hover:text-red-600"
                              data-testid={`quota-remove-${i}`}
                              aria-label="Remove"
                            >
                              <X size={14} weight="bold" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {isManager && (
                <div className="border-t border-neutral-200 px-3 py-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={addRow}
                    className="text-xs font-medium underline underline-offset-4 hover:text-neutral-500 flex items-center gap-1"
                    data-testid="quota-add-row"
                  >
                    <Plus size={12} weight="bold" /> Add row
                  </button>
                  <div className="text-[10px] font-mono text-neutral-400 uppercase tracking-wider">
                    {rows.length} row{rows.length === 1 ? "" : "s"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">
            {isManager ? "Cancel" : "Close"}
          </Button>
          {isManager && (
            <Button
              onClick={save}
              disabled={busy}
              data-testid="quotas-save"
              className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800"
            >
              {busy ? "Saving…" : "Save quotas"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Bulk-set the same per-part quota across many engineers.
 * Manager-only. Applies to ALL field staff by default; the manager can
 * also narrow the scope by selecting individual engineers.
 */
export function BulkQuotaDialog({ open, engineers, onClose, onSaved }) {
  const [part, setPart] = useState({ stock_code: null, name: "", group: null, uom: null });
  const [maxQty, setMaxQty] = useState("5");
  const [scope, setScope] = useState("all");  // 'all' or 'select'
  const [selected, setSelected] = useState({}); // {id: true}
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setPart({ stock_code: null, name: "", group: null, uom: null });
      setMaxQty("5");
      setScope("all");
      setSelected({});
      setFilter("");
    }
  }, [open]);

  const filtered = (engineers || []).filter((e) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return e.name.toLowerCase().includes(q)
      || (e.employee_id || "").toLowerCase().includes(q)
      || (e.region || "").toLowerCase().includes(q);
  });

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleAll = () => {
    if (filtered.every((e) => selected[e.id])) {
      const next = { ...selected };
      filtered.forEach((e) => { delete next[e.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      filtered.forEach((e) => { next[e.id] = true; });
      setSelected(next);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const submit = async () => {
    if (!part.name.trim()) return toast.error("Pick a part first");
    const max = parseInt(maxQty, 10);
    if (!max || max <= 0) return toast.error("Max qty must be > 0");
    if (scope === "select" && selectedCount === 0) return toast.error("Select at least one engineer, or switch to All");
    setBusy(true);
    try {
      const body = {
        stock_code: part.stock_code || null,
        name: part.name.trim(),
        group: part.group || null,
        uom: part.uom || null,
        max_qty: max,
      };
      if (scope === "select") {
        body.apply_to = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      }
      const res = await api.post("/engineers/part-quotas/bulk", body);
      const { created = 0, updated = 0, engineers: n = 0 } = res.data;
      toast.success(`Applied to ${n} engineer(s) — ${created} new, ${updated} updated`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none border-neutral-900 max-h-[90vh] overflow-y-auto" data-testid="bulk-quota-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Bulk Set Quota</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-[11px] text-neutral-600">
            Apply the <b>same max quota</b> for a specific part to many engineers at once — perfect for a fleet-wide rule like <span className="font-mono">5 Hoses</span> or <span className="font-mono">100 Filters</span>. Re-running with a new max updates existing rows.
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="label-eyebrow">Part</Label>
              <StockPartCombobox
                value={part}
                onChange={(picked) => setPart({
                  stock_code: picked.stock_code || null,
                  name: picked.name || "",
                  group: picked.group || null,
                  uom: picked.uom || null,
                })}
                testid="bulk-part-picker"
                placeholder="Search master list…"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Max qty per person</Label>
              <Input
                type="number"
                min={1}
                value={maxQty}
                onChange={(e) => setMaxQty(e.target.value)}
                className="rounded-none border-neutral-400 font-mono text-right"
                data-testid="bulk-max-qty"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="label-eyebrow">Apply to</Label>
            <div className="flex border border-neutral-300" data-testid="bulk-scope">
              {[
                { key: "all",    label: `All field staff (${(engineers || []).length})` },
                { key: "select", label: `Select engineers${selectedCount ? ` · ${selectedCount}` : ""}` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setScope(tab.key)}
                  className={`h-9 px-3 text-xs font-mono uppercase tracking-wider border-r last:border-r-0 border-neutral-300 flex-1 ${
                    scope === tab.key ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-50"
                  }`}
                  data-testid={`bulk-scope-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {scope === "select" && (
              <div className="border border-neutral-200">
                <div className="border-b border-neutral-200 px-3 py-2 flex items-center gap-2">
                  <Input
                    placeholder="Filter by name / employee ID / region…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="h-8 rounded-none border-neutral-300"
                    data-testid="bulk-filter"
                  />
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 hover:bg-neutral-900 hover:text-white whitespace-nowrap"
                    data-testid="bulk-toggle-all"
                  >
                    Toggle all
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filtered.length === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-neutral-400">No engineers match.</div>
                  )}
                  {filtered.map((e) => (
                    <label
                      key={e.id}
                      className="flex items-center gap-2 px-3 py-1.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 cursor-pointer text-sm"
                      data-testid={`bulk-eng-${e.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[e.id]}
                        onChange={() => toggle(e.id)}
                        className="accent-neutral-900"
                      />
                      <span className="flex-1">{e.name}</span>
                      <span className="font-mono text-[10px] text-neutral-500">{e.employee_id || ""}</span>
                      <span className="text-[10px] text-neutral-400">{e.region || ""}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none border-neutral-900 hover:bg-neutral-900 hover:text-white">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !part.name.trim() || !maxQty}
            data-testid="bulk-submit"
            className="rounded-none bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          >
            {busy
              ? "Applying…"
              : scope === "all"
                ? `Apply to all ${(engineers || []).length}`
                : `Apply to ${selectedCount || 0} selected`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
